/**
 * DEV-1483 / DEV-961 / ADR-006 — the ONE workshop predicate.
 *
 * A "workshop" is a user's auto-provisioned private space: a community row whose
 * `personal_owner_user_guid` is set. It is technically a community for the
 * project-creation flow, but it is NOT functionally a community in the UI.
 *
 * THE DISTINCTION THAT MATTERS: readable ≠ discoverable.
 *
 * Privacy already works — a workshop's *contents* are gated by the normal
 * visibility + membership model, and that is a separate concern (DEV-890).
 * What leaks is REACH: the workshop appearing as a thing you can find. So the
 * exclusion here is CATEGORICAL — it applies to the owner and to sysadmins too.
 * Your workshop is reachable from your own spaces; search is not its door, and a
 * sysadmin who needs to see one uses the admin console, which keeps its own
 * visibility rules.
 *
 * WHY THIS FILE EXISTS. The same line — `if (c.personal_owner_user_guid) return
 * false` — was hand-copied into the Discover catalog (catalogScope.ts) and the
 * geo tree (geoTree.ts), and simply never made it into GlobalSearch or the globe
 * hotspots. That is the failure mode of a copied predicate: the copies drift and
 * the omissions are silent. One definition, imported everywhere.
 *
 * The predicate keys ONLY on `personal_owner_user_guid` — never on entity_type,
 * name, or visibility — so no legitimate geo or org community can be caught by it.
 */

/** The minimum shape needed to answer the question. Both wire casings accepted. */
export interface WorkshopCandidate {
  personal_owner_user_guid?: string | null;
  personalOwnerUserGuid?: string | null;
}

/** Is this community a user's personal workshop? */
export function isWorkshop(c: WorkshopCandidate | null | undefined): boolean {
  if (!c) return false;
  const owner = c.personal_owner_user_guid ?? c.personalOwnerUserGuid ?? null;
  return !!owner && String(owner).trim() !== '';
}

/**
 * May this community appear in a DISCOVERY surface — search, the Discover
 * catalog, the geo tree, globe hotspots, any "browse communities" list?
 *
 * This is deliberately the inverse of isWorkshop and nothing more: it does NOT
 * fold in visibility, is_active, or entity-type rules, because those are each
 * surface-specific and already handled where they belong. Conflating them here
 * is how a predicate grows into a god-function nobody can safely change.
 */
export function isDiscoverable(c: WorkshopCandidate | null | undefined): boolean {
  return !isWorkshop(c);
}
